import React, { useState, useRef, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import './Contact.scss';

// ── EmailJS config ─────────────────────────────────────────────────────────
// 1. Sign up free at https://www.emailjs.com
// 2. Add an Email Service (Gmail) → copy the Service ID below
// 3. Create an Email Template     → copy the Template ID below
// 4. Go to Account → API Keys     → copy the Public Key below
// Template variables used: {{from_name}}, {{company}}, {{from_email}},
//   {{phone}}, {{product}}, {{message}}, {{to_email}}
const EMAILJS_SERVICE_ID  = 'service_4znnvvl';   // ← replace after setup
const EMAILJS_TEMPLATE_ID = 'template_9717off';  // ← replace after setup
const EMAILJS_PUBLIC_KEY  = 'wZ8Ts2lXHZJfxbZMB';   // ← replace after setup
const TO_EMAIL            = 'aavjoindustries@gmail.com'; // ← change for production

const infoItems = [
  { icon: '📞', label: 'Phone',   value: '+91 9328058417', href: 'tel:+919328058417' },
  { icon: '📞', label: 'Alt Phone',   value: '+91 9081041094', href: 'tel:+919081041094' },
  { icon: '✉️', label: 'Email',   value: 'aavjoindustries@gmail.com', href: 'mailto:aavjoindustries@gmail.com' },
  { icon: '📍', label: 'Address', value: '42, Syamved Industrial Estate, SP Ring Road, Ranasan, Opposite Gamthi Hotel, GIDC Naroda, Ahmedabad – 382340, Gujarat, India' },
  { icon: '📱', label: 'Instagram',  value: '@aavjoindustries', href: 'https://instagram.com/aavjoindustries?igsh=MWpvYWYya3dxbzBuNg==' },
];

const socialLinks = [
  {
    icon: faFacebookF,
    label: 'Facebook',
    href: 'https://facebook.com/share/1FJZ7yDuuS',
  },
  {
    icon: faWhatsapp,
    label: 'WhatsApp',
    href: 'https://wa.me/919328058417?partnertoken=eyJhbGciOiJFUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjE3NjIwNzY3MzIsImlhdCI6MTc2MjA3NjQzMiwidXJsIjoiaHR0cHM6Ly93YS5tZS85MTkzMjgwNTg0MTciLCJpc3MiOiJHb29nbGUifQ.82VvkF5t3hAzjfZ-W9VgMk3P1unfnW5-1k03Ya3AAB5zLYXjvVSYp-CgAutZRVWWBrngO3WflC-9jNJncHC7Lg',
  },
  {
    icon: faInstagram,
    label: 'Instagram',
    href: 'https://instagram.com/aavjoindustries?igsh=MWpvYWYya3dxbzBuNg==',
  },
  {
    icon: faEnvelope,
    label: 'Email',
    href: 'mailto:aavjoindustries@gmail.com',
  },
];
const initialForm = { name: '', company: '', email: '', phone: '', product: '', message: '' };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateForm = (values) => {
  const errors = {};
  if (!values.name.trim()) {
    errors.name = 'Full name is required.';
  }
  if (!values.email.trim()) {
    errors.email = 'Email address is required.';
  } else if (!EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }
  if (!values.message.trim()) {
    errors.message = 'Message / requirements is required.';
  }
  return errors;
};

const Contact = () => {
  const formRef                     = useRef(null);
  const [form,      setForm]        = useState(initialForm);
  const [submitted, setSubmitted]   = useState(false);
  const [loading,   setLoading]     = useState(false);
  const [error,     setError]       = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const product = params.get('product');
    if (product) {
      setForm((prev) => ({ ...prev, product }));
    }
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    const validationErrors = validateForm(form);
    if (Object.keys(validationErrors).length) {
      setFieldErrors(validationErrors);
      return;
    }

    setFieldErrors({});
    setLoading(true);

    const templateParams = {
      from_name:   form.name,
      company:     form.company || '—',
      from_email:  form.email,
      phone:       form.phone   || '—',
      product:     form.product || '—',
      message:     form.message,
      reply_to:    form.email,
      to_email:    TO_EMAIL,
    };

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      setSubmitted(true);
      setForm(initialForm);
      setFieldErrors({});
    } catch (err) {
      console.error('EmailJS error status:', err?.status);
      console.error('EmailJS error text:', err?.text);
      console.error('EmailJS full error:', err);

      const reason = err?.text || err?.message || 'Unknown error';
      setError(`Failed to send: ${reason}. You can also reach us at aavjoindustries@gmail.com`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="contact-section" id="contact">
      <div className="container">
        <div className="section-header">
          <span className="badge-label">Contact Us</span>
          <h2>Let's Build Something Great Together</h2>
          <div className="section-divider" />
          <p>
            Share your requirements and our team will respond
            with a tailored solution proposal within 24 hours.
          </p>
        </div>

        <div className="row g-4 align-items-stretch">

          {/* Info panel */}
          <div className="col-lg-5">
            <div className="contact-info">
              <div className="info-header">
                <h3>Get in Touch</h3>
                <p>
                  Whether it's a quick query or a full project brief —
                  we're always ready to talk.
                </p>
              </div>

              <div className="info-items">
                {infoItems.map(item => (
                  <div key={item.label} className="info-item">
                    <div className="info-icon">{item.icon}</div>
                    <div className="info-body">
                      <div className="info-label">{item.label}</div>
                      <div className="info-value">
                        {item.href ? (
                          <a href={item.href} className="contact-link">
                            {item.value}
                          </a>
                        ) : (
                          item.value
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="social-row">
                {socialLinks.map(s => (
                  <a
                    key={s.label}
                    className="social-btn"
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.label}
                    title={s.label}
                  >
                    <FontAwesomeIcon icon={s.icon} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Form panel */}
          <div className="col-lg-7">
            <div className="contact-form-wrap">
              {submitted ? (
                <div className="success-msg">
                  <span className="success-icon">✅</span>
                  <h4>Enquiry Sent!</h4>
                  <p>
                    Thank you for reaching out. Our team will review your
                    requirements and contact you within one business day.
                  </p>
                  <button
                    className="submit-btn"
                    style={{ marginTop: '1.5rem', width: 'auto', padding: '.7rem 2rem' }}
                    onClick={() => setSubmitted(false)}
                  >
                    Send Another Enquiry
                  </button>
                </div>
              ) : (
                <form ref={formRef} onSubmit={handleSubmit} noValidate>

                  <div className="form-row">
                    <div>
                      <label className="form-label-custom">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Ramesh Kumar"
                        className={`form-control-custom${fieldErrors.name ? ' is-invalid' : ''}`}
                        aria-invalid={!!fieldErrors.name}
                      />
                      {fieldErrors.name && (
                        <span className="field-error">{fieldErrors.name}</span>
                      )}
                    </div>
                    <div>
                      <label className="form-label-custom">Company</label>
                      <input
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Acme Industries Pvt. Ltd."
                        className="form-control-custom"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div>
                      <label className="form-label-custom">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="you@company.com"
                        className={`form-control-custom${fieldErrors.email ? ' is-invalid' : ''}`}
                        aria-invalid={!!fieldErrors.email}
                      />
                      {fieldErrors.email && (
                        <span className="field-error">{fieldErrors.email}</span>
                      )}
                    </div>
                    <div>
                      <label className="form-label-custom">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+91 98765 43210"
                        className="form-control-custom"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label-custom">Product / Service of Interest</label>
                    <select
                      name="product"
                      value={form.product}
                      onChange={handleChange}
                      className="form-control-custom"
                    >
                      <option value="">— Select an option —</option>
                      <option>Aerial Lift</option>
                      <option>Scissor Lift</option>
                      <option>Goods Lift</option>
                      <option>Stacker</option>
                      <option>Pallet Truck</option>
                      <option>Hydraulic Lifting Solutions</option>
                      <option>Material Handling Equipment</option>
                      <option>Custom / Turnkey Solution</option>
                      <option>Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label-custom">Message / Requirements *</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      placeholder="Describe your requirements…"
                      className={`form-control-custom textarea${fieldErrors.message ? ' is-invalid' : ''}`}
                      aria-invalid={!!fieldErrors.message}
                    />
                    {fieldErrors.message && (
                      <span className="field-error">{fieldErrors.message}</span>
                    )}
                  </div>

                  {error && (
                    <div className="error-msg">{error}</div>
                  )}

                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={loading}
                  >
                    {loading ? 'Sending…' : 'Send Enquiry →'}
                  </button>
                </form>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Contact;
